import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Modal from "../../../../../parts/modal-new";
import { getDateWithDayToDisplay } from "../../../../../utils/date-helpers";
import Select from "../../../../../parts/inputs/select";
import { simpleHash } from "../../../../../utils/string-helpers";

export const MoveMealPlanModal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const { query, mealPlan } = locals;
  const {
    date,
    athlete,
    mealPlan: mealPlanId,
    week = 1,
    dayType,
    optionId,
  } = query;
  const planMode = mealPlan?.planMode || "detailed";
  const { weeks = [], trainingDayOptions = [], restDayOptions = [] } = mealPlan;
  const dateToUse = athlete ? date : `Week ${week} ${date}`;
  const isSimplified = planMode === "simplified";

  // Build allDates based on planMode
  const allDates =
    isSimplified && dayType
      ? // Simplified: use options from trainingDayOptions or restDayOptions
        (dayType === "training" ? trainingDayOptions : restDayOptions).map(
          (option, index) => ({
            text: option.title || `Option ${index + 1}`,
            value: option.title || `Option ${index + 1}`,
          })
        )
      : // Detailed: use weeks structure
        weeks.flatMap(({ days }, weekIndex) =>
          days.map(({ date }, index) => ({
            text: date
              ? getDateWithDayToDisplay(date)
              : `Week ${weekIndex + 1} Day ${index + 1}`,
            value: date ?? `Week ${weekIndex + 1} Day ${index + 1}`,
          }))
        );

  const [endDateState, setEndDateState] = useState(dateToUse);

  // Calculate dynamic height class based on number of options
  const getHeightClass = () => {
    const optionsCount = allDates.length;
    if (optionsCount < 3) return "options-count-less-than-3";
    if (optionsCount === 3) return "options-count-3";
    return "options-count-more-than-3";
  };

  /**
   * Close modal
   */
  const handleCancel = () => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: { "modal[type]": "", date: "" },
    });
  };

  /**
   * Select new date
   * @param {*} newVal
   */
  const handleSelectChange = (newVal) => {
    setEndDateState(newVal);
  };

  /**
   * Send information to the API
   */
  const handleSubmit = () => {
    const payload = {
      initDate: dateToUse,
      endDate: endDateState,
      bodyAction: "handle-day",
      handle: "move",
      isTemplate: athlete ? false : true,
    };

    // Add simplified-specific data if needed
    if (isSimplified && dayType && optionId) {
      // Find source and target options by optionId
      const optionsArray =
        dayType === "training" ? trainingDayOptions : restDayOptions;
      const sourceOption = optionsArray.find(
        (opt) => opt.optionId?.toString() === optionId?.toString()
      );
      
      if (!sourceOption) {
        console.error("Source option not found");
        return;
      }

      // Find target option by matching title with endDateState
      const targetOption = optionsArray.find((opt, idx) => {
        const optTitle = opt.title || `Option ${idx + 1}`;
        return optTitle === endDateState;
      });

      if (targetOption) {
        payload.planMode = "simplified";
        payload.dayType = dayType;
        payload.sourceOptionId = optionId;
        payload.targetOptionId = targetOption.optionId;
      }
    }

    const requestId = simpleHash(JSON.stringify(payload));

    const body = {
      ...payload,
      requestId,
    };

    submit(
      `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
      {
        method: "POST",
        body,
      }
    );
  };

  return (
    <Modal
      className="move-meal-plan-modal"
      title={t("editMealPlan.moveMealPlanModal.title")}
      toClear={["date", "dayType", "optionId"]}
    >
      <div className="body">
        <div className="info">
          <span className="label">{isSimplified ? t("editMealPlan.moveMealPlanModal.option") : t("editMealPlan.moveMealPlanModal.date")}</span>
          <span className="font-bold">
            {getDateWithDayToDisplay(dateToUse)}
          </span>
        </div>
        <div
          className={`${
            dateToUse === endDateState ? "select select-disabled" : "select"
          } ${getHeightClass()}`}
        >
          <span className="label">{t("editMealPlan.moveMealPlanModal.moveTo")}</span>
          <Select
            withFocus
            name="endDate"
            value={endDateState}
            onChange={(newVal) => handleSelectChange(newVal)}
            options={allDates.map(({ text, value }) => {
              return { value, text };
            })}
          />
        </div>
        <div className="buttons">
          <button className="cancel" onClick={handleCancel}>
            {t("editMealPlan.moveMealPlanModal.cancel")}
          </button>
          <button
            data-button-type="primary"
            onClick={handleSubmit}
            disabled={dateToUse === endDateState}
          >
            {t("editMealPlan.moveMealPlanModal.move")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

MoveMealPlanModal.propTypes = {
  locals: PropTypes.object,
};
